'use client';
import React, { forwardRef, useImperativeHandle, useRef } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

export type RecaptchaHandle = {
  /** Token courant du widget, ou null s'il n'a pas encore été résolu. */
  getValue: () => string | null;
  /** Réinitialise le widget (à appeler après une soumission). */
  reset: () => void;
};

export type RecaptchaProps = {
  /** Clé de site publique. Si vide/absente, aucun widget n'est rendu. */
  siteKey?: string | null;
  /** Si false/undefined, aucun widget n'est rendu (captcha non exigé). */
  requireRecaptcha?: boolean | null;
  /** Classe du conteneur — le layout reste piloté par l'appelant. */
  className?: string;
};

/**
 * Widget reCAPTCHA CONTRÔLÉ, sans dépendance à un backend particulier : la clé
 * de site est fournie en prop `siteKey`. Utilisable dans n'importe quel projet
 * Next.js (la clé peut venir d'une prop, d'un contexte, d'un `NEXT_PUBLIC_*`…).
 *
 * Expose `getValue()` / `reset()` au parent via `ref`. Le handle est stable même
 * quand rien n'est rendu (les hooks tournent avant le `return null`), donc
 * `ref.current?.getValue()` est toujours sûr.
 *
 * Pour les apps Payload qui exposent l'endpoint `/api/recaptcha-config`, préférer
 * `PayloadRecaptcha`, qui récupère `siteKey` au runtime et délègue à ce composant.
 */
export const Recaptcha = forwardRef<RecaptchaHandle, RecaptchaProps>(
  function Recaptcha({ siteKey, requireRecaptcha, className }, ref) {
    const widget = useRef<ReCAPTCHA>(null);

    useImperativeHandle(
      ref,
      () => ({
        getValue: () => widget.current?.getValue() ?? null,
        reset: () => widget.current?.reset(),
      }),
      [],
    );

    if (!requireRecaptcha || !siteKey) return null;

    return (
      <div className={className}>
        <ReCAPTCHA sitekey={siteKey} ref={widget} />
      </div>
    );
  },
);
